// THE CLIENT GROUND (rearch-r8k, APP_REARCH §3g ONE GROUND): the client stands,
// aims and waits on the SAME artefact and sampler as the game server
// (game/ground.js): the GROUND-M L0 nodes (groundm/L0/<tx>_<ty>.bin, GM03, the
// bytes the ground tree draws) through vrm_core's Ground (the client wasm,
// wasm.js), so client y == server y by construction. Under a GM03 hole (a bore
// opening) both ends read game/holefall.js over the public terrain/L0 tile,
// fetched only when a sample lands in a hole (ruling (B) 2026-10-31).
//
//   standHeight(x, z) / height(x, z)  THE skin (Player._skinY): the L0 node's
//                     triangle, else the hole fallback; null while the node
//                     (or a hole's tile) is not resident or the wasm is not in
//   normal(x, z)      central differences of the skin (0.75 m)
//   ensureAround(x, z) the 3×3 L0 nodes around (x, z) resident (fetched, added
//                     to the wasm); nodes more than RING_KEEP away go
//   readyAround / whenReady  that block is in (main.js's beam-in latch)
//   own(k, who, bytes) / disown(k, who)  another owner's nodes in the same
//                     wasm Ground (aimray.js AimGround: the aim / label rays);
//                     own resolves once the node is in
//
// THE ADD OFF THE MAIN THREAD (quest-move 2026-11-02): a landed node is
// decoded + indexed by a pool worker (pool-ground.js ground.index, its own
// vrm_core instance) and ADOPTED into the wasm Ground as its GMIX1 index
// (Ground.addIndex, ~1 ms) — the node gm.add builds, bit for bit
// (check-vrm-core §7b); the synchronous add was 11-50 ms a node on the box,
// one long task per crossed node while flying. Until it is adopted the node
// stays 'loading': exactly a fetch still in flight (standHeight null, the
// caller's fallback). A failed worker job falls back to the synchronous add
// once. Without a window (node gates, prep: null) every add is synchronous,
// as before. ensureAround also asks the 3×3 block ahead along the measured
// velocity (LEAD_S, at most LEAD_MAX_M ahead: inside the RING_KEEP ring), so
// a fast mover's next block is in before it gets there.
//   lower(x, z) / upper(x, z)  the L0 node's [y0, y1] from groundm/index.json
//                     (every triangle of the node lies in it): the frame-wide
//                     envelope of the aim march, the label apex and the
//                     conservative depth's lower ground; null before the index
//
// Node key = ty * 65536 + tx on the L0 lattice (game/ground.js _need: a node
// covers x in [0.25 + 128 tx, 0.25 + 128 (tx + 1)); aimray.js l0Key).
import * as THREE from 'three';
import RENDER_CFG from './renderconfig.json';
import { HoleFallback, renderStride } from '../../game/holefall.js';
import { fetchAsset } from './assetq.js';
import { wasmCore } from './wasm.js';
import { groundPrep } from './groundprep.js';

const NODE_M0 = 128;          // an L0 node's side (groundm/lookup.js nodeSide(0))
const RING_KEEP = 2;          // block nodes kept within ±2 of the current block (≤ 25)
const TILE_CAP = 8;           // hole-fallback tiles (132 KB each)
// the index's y0 / y1 are rounded to 3 decimals (groundm/buildnode.mjs toFixed(3)):
// widened by this the box holds every vertex, so every triangle
const BOX_EPS = 0.001;
// the block ahead (ensureAround): the velocity measured between two calls at
// most LEAD_GAP_MS apart (implied speeds past LEAD_VMAX are another caller's
// position, ignored); from LEAD_VMIN on, the block LEAD_S of travel ahead,
// at most LEAD_MAX_M (one node: its 3×3 stays inside the ±RING_KEEP ring)
const LEAD_S = 1.5, LEAD_MAX_M = NODE_M0, LEAD_VMIN = 8, LEAD_VMAX = 2000, LEAD_GAP_MS = 500;
const _clock = typeof performance !== 'undefined' ? () => performance.now() : () => Date.now();

export const nodeKey = (x, z) => Math.floor((z - 0.25) / NODE_M0) * 65536 + Math.floor((x - 0.25) / NODE_M0);

export class ClientGround {
  // meta: the bake's meta.json; baseUrl: /bake/<id>; index: a promise of the
  // parsed groundm/index.json (GroundTree.indexRaw, no second fetch) | null;
  // core: a promise of the vrm_core glue's Core (default wasmCore('vrm_core'));
  // prep: bytes -> Promise of the node's GMIX1 index (default: the runner
  // pool.js registered in groundprep.js, read when a node lands, in a window
  // only; none = the synchronous add)
  constructor(meta, baseUrl, { index = null, core = null, prep = undefined } = {}) {
    this.meta = meta;
    this.baseUrl = baseUrl;
    const bb = meta.bbox;
    this.extentX = bb.e1 - bb.e0;
    this.extentZ = bb.n1 - bb.n0;
    this.level = meta.levels.L0;
    this.gm = null;                  // vrm_core Ground (once the wasm is in)
    this.nodes = new Map();          // key -> { st: 'loading' | 'in' | 'none', bytes, ix (GMIX1 before the wasm is in), owners: Set }
    this.prep = prep;                // undefined = the registered runner (_land)
    this._lv = null;                 // ensureAround's last position {x, z, t} (the lead's velocity)
    this.block = -1;                 // the key of the block's centre node
    this._waiters = [];              // whenReady: { key, res }
    this._tiles = new Map();         // hole tiles: ty * nx + tx -> { t: Uint16Array | null | 'loading', used }
    this._tick = 0;
    this._stale = new Set();         // tile keys in flight a world swap invalidated
    this.gen = 0;                    // bumps when a node or a hole tile lands
    this.st = { nodeLoads: 0, nodeFails: 0, holes: 0, tileLoads: 0, prepped: 0, prepFails: 0, lead: 0 };
    this.hole = new HoleFallback(meta, (tx, ty) => this._tile(tx, ty), renderStride(RENDER_CFG));
    this.env = null;                 // { nx, ny, y0: Float64Array, y1 } (NaN = no node)
    if (index) Promise.resolve(index).then((ix) => this._envOf(ix)).catch((e) => console.error('[ground] index', e));
    this.coreP = Promise.resolve(core || wasmCore('vrm_core', { prio: 'near' })).then((c) => {
      this.gm = c.ground();
      for (const [k, n] of this.nodes) if (n.st === 'in' && (n.bytes || n.ix)) this._add(k, n);
      this._settle();
      return c;
    });
    this.coreP.catch((e) => console.error('[ground] vrm_core', e));
  }

  // the bake's platforms (ways2.js onPlatforms: a game/pave.js platformsIndex)
  set platforms(p) { this.hole.platforms = p; }
  get platforms() { return this.hole.platforms; }

  // ------------------------------------------------------------ nodes
  _fetch(k) {
    const tx = k % 65536, ty = (k - tx) / 65536;
    const n = { st: 'loading', bytes: null, owners: new Set() };
    this.nodes.set(k, n);
    // the ground tree's url (groundtree.js): the HTTP / service worker cache answers a second read
    fetchAsset(`${this.baseUrl}/groundm/L0/${tx}_${ty}.bin`, { prio: 'near', status: true }).then((r) => {
      if (this.nodes.get(k) !== n) return;             // dropped in flight
      if (r.status === 404) { n.st = 'none'; this._settle(); return; }
      if (!r.body) throw new Error(`HTTP ${r.status}`);
      n.bytes = new Uint8Array(r.body);
      this.st.nodeLoads++;
      return this._land(k, n);
    }).catch(() => {
      if (this.nodes.get(k) !== n) return;
      this.nodes.delete(k);
      this.st.nodeFails++;
      if (this._waiters.length) setTimeout(() => this._settle(), 1000);
    });
    return n;
  }

  // a node's bytes are in: 'in' once the wasm holds it (prep: after the
  // worker's index; its failure: the synchronous add) -> a promise when
  // prepped (resolves once in; nothing when the node was dropped meanwhile)
  _land(k, n) {
    const done = () => { n.st = 'in'; if (this.gm) this._add(k, n); this.gen++; this._settle(); };
    const prep = this.prep !== undefined ? this.prep : (typeof window !== 'undefined' ? groundPrep() : null);
    if (!prep) { done(); return undefined; }
    return prep(n.bytes).then((ix) => {
      if (this.nodes.get(k) !== n) return;           // dropped while indexed
      this.st.prepped++;
      n.ix = ix; n.bytes = null;
      done();
    }, () => {
      if (this.nodes.get(k) !== n) return;
      this.st.prepFails++;
      done();                                        // the synchronous add (refused bytes throw here, as before)
    });
  }

  _add(k, n) {
    if (n.ix) this.gm.addIndex(n.ix);
    else this.gm.add(n.bytes);
    n.bytes = n.ix = null;           // the wasm holds the node
  }

  _inGrid(tx, ty) { return tx >= 0 && ty >= 0 && tx < this.level.nx && ty < this.level.ny; }

  _need(k) {
    const n = this.nodes.get(k);
    if (n) return n;
    const tx = k % 65536, ty = (k - tx) / 65536;
    return this._inGrid(tx, ty) ? this._fetch(k) : null;
  }

  _drop(k) {
    const n = this.nodes.get(k);
    if (!n) return;
    this.nodes.delete(k);
    if (n.st === 'in' && !n.bytes && !n.ix && this.gm) {
      const tx = k % 65536;
      this.gm.remove(0, tx, (k - tx) / 65536);
    }
  }

  // another owner's node (aimray.js AimGround): fetched by the caller, held
  // until disowned (a block node is never dropped under it, nor it under one)
  own(k, who, bytes) {
    let n = this.nodes.get(k);
    if (n) { n.owners.add(who); return Promise.resolve(); }
    n = { st: 'loading', bytes: new Uint8Array(bytes), owners: new Set([who]) };
    this.nodes.set(k, n);
    try {
      return Promise.resolve(this._land(k, n)).catch((e) => { if (this.nodes.get(k) === n) this.nodes.delete(k); throw e; });
    } catch (e) {                    // the synchronous add refused the bytes
      this.nodes.delete(k);
      throw e;
    }
  }

  disown(k, who) {
    const n = this.nodes.get(k);
    if (!n || !n.owners.delete(who) || n.owners.size || this._inBlock(k, RING_KEEP)) return;
    this._drop(k);
  }

  holds(k) { const n = this.nodes.get(k); return !!(n && n.st !== 'loading'); }

  _inBlock(k, r) {
    if (this.block < 0) return false;
    const tx = k % 65536, ty = (k - tx) / 65536, bx = this.block % 65536, by = (this.block - bx) / 65536;
    return Math.abs(tx - bx) <= r && Math.abs(ty - by) <= r;
  }

  ensureAround(x, z) {
    const c = nodeKey(x, z), cx = c % 65536, cy = (c - cx) / 65536;
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) this._need((cy + dy) * 65536 + cx + dx);
    this._lead(x, z, c);
    if (c === this.block) return;
    this.block = c;
    // a whenReady for another block is released (false): its caller re-asks
    for (let i = this._waiters.length - 1; i >= 0; i--) {
      const w = this._waiters[i];
      if (w.key !== c) { this._waiters.splice(i, 1); w.res(false); }
    }
    for (const [k, n] of [...this.nodes]) if (!n.owners.size && !this._inBlock(k, RING_KEEP)) this._drop(k);
  }

  // the 3×3 block LEAD_S ahead along the velocity since the last call (see LEAD_*)
  _lead(x, z, c) {
    const t = _clock(), L = this._lv;
    if (!L || t - L.t > LEAD_GAP_MS) { this._lv = { x, z, t }; return; }
    const dt = (t - L.t) / 1000;
    if (!(dt > 0)) return;
    const vx = (x - L.x) / dt, vz = (z - L.z) / dt, v = Math.hypot(vx, vz);
    if (v > LEAD_VMAX) return;                       // another caller's position: keep the chain
    L.x = x; L.z = z; L.t = t;
    if (v < LEAD_VMIN) return;
    const d = Math.min(LEAD_S * v, LEAD_MAX_M) / v;
    const a = nodeKey(x + vx * d, z + vz * d);
    if (a === c) return;
    const ax = a % 65536, ay = (a - ax) / 65536;
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const k = (ay + dy) * 65536 + ax + dx;
        if (!this.nodes.has(k)) { this.st.lead++; this._need(k); }
      }
    }
  }

  _blockReady(c) {
    if (!this.gm) return false;
    const cx = c % 65536, cy = (c - cx) / 65536;
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      const tx = cx + dx, ty = cy + dy;
      if (!this._inGrid(tx, ty)) continue;
      const n = this.nodes.get(ty * 65536 + tx);
      if (!n || n.st === 'loading') return false;
    }
    return true;
  }

  // the 3×3 block ensureAround(x, z) asks for is in (and the wasm)
  readyAround(x, z) { return this._blockReady(nodeKey(x, z)); }

  whenReady(x, z) {
    const key = nodeKey(x, z);
    if (this._blockReady(key)) return Promise.resolve(true);
    return new Promise((res) => {
      this._waiters.push({ key, res });
      this._settle();
    });
  }

  _settle() {
    for (let i = this._waiters.length - 1; i >= 0; i--) {
      const w = this._waiters[i];
      if (this._blockReady(w.key)) { this._waiters.splice(i, 1); w.res(true); continue; }
      const cx = w.key % 65536, cy = (w.key - cx) / 65536;
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) this._need((cy + dy) * 65536 + cx + dx);
    }
  }

  // ------------------------------------------------------------ the skin
  // THE standing skin at (x, z) (game/ground.js skin's twin), or null
  standHeight(x, z) {
    if (!this.gm) return null;
    const n = this.nodes.get(nodeKey(x, z));
    if (!n || n.st === 'loading') return null;
    if (n.st === 'in') {
      const s = this.gm.skinY(x, z);
      if (s !== null) return s;
    }
    this.st.holes++;
    return this.hole.stand(x, z);
  }

  // one ground: every height read is the skin
  height(x, z) { return this.standHeight(x, z); }

  ready(x, z) { return this.standHeight(x, z) !== null; }

  normal(x, z, out) {
    const e = 0.75;
    const c = this.height(x, z);
    if (c === null) return null;
    const hx1 = this.height(x + e, z), hx0 = this.height(x - e, z);
    const hz1 = this.height(x, z + e), hz0 = this.height(x, z - e);
    if (hx1 === null || hx0 === null || hz1 === null || hz0 === null) return null;
    out = out || new THREE.Vector3();
    out.set(-(hx1 - hx0) / (2 * e), 1, -(hz1 - hz0) / (2 * e)).normalize();
    return out;
  }

  // ------------------------------------------------------------ hole tiles
  // the public terrain/L0 tile for game/holefall.js: fetched on the first
  // hole sample that needs it (undefined until it lands), LRU TILE_CAP
  _tile(tx, ty) {
    const k = ty * this.level.nx + tx;
    let e = this._tiles.get(k);
    if (!e) {
      if (this._tiles.size >= TILE_CAP) {
        let kMin = null, uMin = Infinity;
        for (const [kk, ee] of this._tiles) if (ee.t !== 'loading' && ee.used < uMin) { uMin = ee.used; kMin = kk; }
        if (kMin !== null) this._tiles.delete(kMin);
      }
      e = { t: 'loading', used: 0 };
      this._tiles.set(k, e);
      fetchAsset(`${this.baseUrl}/terrain/L0/${tx}_${ty}.bin`, { prio: 'near', as: 'arrayBuffer' }).then((ab) => {
        if (this._stale.delete(k)) { this._tiles.delete(k); return; }
        e.t = ab ? new Uint16Array(ab) : null;
        this.st.tileLoads++;
        this.gen++;
      }).catch(() => { this._stale.delete(k); this._tiles.delete(k); });
    }
    e.used = ++this._tick;
    return e.t && e.t !== 'loading' ? e.t : undefined;
  }

  // R8h2 world swap (worldswap.js terrainHook): the hole tiles of `rels`
  // (terrain/L0/<tx>_<ty>.bin) moved — a resident one goes, one in flight is
  // dropped as it lands; the next hole sample fetches it again
  invalidateTiles(rels) {
    for (const r of rels) {
      const m = /^terrain\/L0\/(\d+)_(\d+)\.bin$/.exec(r);
      if (!m) continue;
      const tx = Number(m[1]), ty = Number(m[2]);
      if (!this._inGrid(tx, ty)) continue;
      const k = ty * this.level.nx + tx, e = this._tiles.get(k);
      if (e && e.t === 'loading') this._stale.add(k);
      else this._tiles.delete(k);
    }
  }

  // ------------------------------------------------------------ the envelope
  _envOf(ix) {
    const nx = ix.nx || this.level.nx, ny = ix.ny || this.level.ny;
    const y0 = new Float64Array(nx * ny).fill(NaN), y1 = new Float64Array(nx * ny).fill(NaN);
    for (const n of Array.isArray(ix.nodes) ? ix.nodes : Object.values(ix.nodes)) {
      if (n.k !== 0 || n.tx < 0 || n.ty < 0 || n.tx >= nx || n.ty >= ny) continue;
      y0[n.ty * nx + n.tx] = n.y0 - BOX_EPS;
      y1[n.ty * nx + n.tx] = n.y1 + BOX_EPS;
    }
    this.env = { nx, ny, y0, y1, step: NODE_M0 };
    this.gen++;
  }

  _box(x, z, a) {
    const E = this.env;
    if (!E) return null;
    const tx = Math.floor((x - 0.25) / NODE_M0), ty = Math.floor((z - 0.25) / NODE_M0);
    if (tx < 0 || ty < 0 || tx >= E.nx || ty >= E.ny) return null;
    const v = a[ty * E.nx + tx];
    return v === v ? v : null;
  }

  // the L0 node's lowest / highest vertex at (x, z) (every triangle between)
  lower(x, z) { return this.env ? this._box(x, z, this.env.y0) : null; }
  upper(x, z) { return this.env ? this._box(x, z, this.env.y1) : null; }

  stats() {
    let block = 0, other = 0;
    for (const n of this.nodes.values()) if (n.st === 'in') { if (n.owners.size) other++; else block++; }
    return { ...this.st, block, owned: other, tiles: this._tiles.size };
  }
}
